import { useEffect, useMemo, useState } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  MinimalDatePicker,
  MinimalSegmentedControl,
  MinimalTimePicker,
  type MinimalOption,
} from '@ovasabi/ui-minimal'
import { ClockIcon } from '../icons'
import {
  addLocalDays,
  combineScheduledAt,
  formatScheduled,
  minimumTimeForDate,
  nextAvailableDelivery,
  splitScheduledAt,
  toLocalDateKey,
  toLocalTimeKey,
} from '../../lib/scheduling'

type ScheduleMode = 'asap' | 'scheduled'

const MODE_OPTIONS: readonly MinimalOption<ScheduleMode>[] = [
  { value: 'asap', label: 'As soon as possible' },
  { value: 'scheduled', label: 'Schedule for later' },
]

export interface SchedulePickerProps {
  /** Currently selected `scheduled_at` (ISO) — empty string means on-demand. */
  value: string
  onChange: (scheduledAt: string) => void
  /** Override the leading label (defaults to "Delivery time"). */
  label?: string
  pickup?: boolean
  minimumLeadMinutes?: number
  intervalMinutes?: number
  horizonDays?: number
  serviceStart?: string
  serviceEnd?: string
  locale?: string
  /** Deterministic clock for tests and server-rendered previews. */
  now?: Date
}

const Style = {
  Wrap: styled.section`
    display: grid;
    gap: ${minimalVars.space.xs};
    padding: ${minimalVars.space.xs};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-left: 2px solid ${minimalVars.color.brand};
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurface};
  `,
  Head: styled.span`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Pickers: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(10rem, 0.65fr);
    gap: ${minimalVars.space.sm};
    align-items: start;

    /* 560px matches no named stop; it sits between the hand and page stops. */
    @media (max-width: 560px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  Summary: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: ${minimalVars.typography.lineHeightBody};
  `,
}

/**
 * Controlled delivery scheduler. Chow owns lead time and service-window rules;
 * Foundation owns the accessible mode, date-popover, and time-picker behavior.
 */
export const SchedulePicker = ({
  value,
  onChange,
  pickup = false,
  label = pickup ? 'Pickup time' : 'Delivery time',
  minimumLeadMinutes = 120,
  intervalMinutes = 30,
  horizonDays = 14,
  serviceStart = '08:00',
  serviceEnd = '22:00',
  locale = 'en-NG',
  now,
}: SchedulePickerProps) => {
  const [clock, setClock] = useState(() => now ?? new Date())

  useEffect(() => {
    if (now) return undefined
    const id = window.setInterval(() => setClock(new Date()), 60_000)
    return () => window.clearInterval(id)
  }, [now])

  const referenceNow = now ?? clock
  const schedulingWindow = useMemo(
    () => ({ minimumLeadMinutes, intervalMinutes, serviceStart, serviceEnd }),
    [minimumLeadMinutes, intervalMinutes, serviceStart, serviceEnd],
  )
  const earliest = useMemo(
    () => nextAvailableDelivery(referenceNow, schedulingWindow),
    [referenceNow, schedulingWindow],
  )
  const latest = useMemo(
    () =>
      addLocalDays(
        earliest,
        Number.isFinite(horizonDays)
          ? Math.min(90, Math.max(1, Math.trunc(horizonDays)))
          : 14,
      ),
    [earliest, horizonDays],
  )

  const selectedInstant = value ? new Date(value) : null
  const usableSelection =
    selectedInstant && !Number.isNaN(selectedInstant.getTime()) && selectedInstant >= earliest
      ? selectedInstant
      : earliest
  const parts = splitScheduledAt(usableSelection.toISOString()) ?? {
    date: toLocalDateKey(earliest),
    time: toLocalTimeKey(earliest),
  }
  const mode: ScheduleMode = value ? 'scheduled' : 'asap'
  const minimumTime = minimumTimeForDate(parts.date, referenceNow, schedulingWindow) ?? serviceStart

  const emitParts = (date: string, time: string) => {
    const next = combineScheduledAt(date, time)
    if (next) onChange(next)
  }

  const handleModeChange = (next: ScheduleMode) => {
    onChange(next === 'asap' ? '' : earliest.toISOString())
  }

  return (
    <Style.Wrap aria-label={label}>
      <Style.Head>
        <ClockIcon size="sm" />
        {label}
      </Style.Head>
      <MinimalSegmentedControl
        value={mode}
        options={MODE_OPTIONS}
        onChange={handleModeChange}
        ariaLabel={`${label} mode`}
        size="md"
      />
      {mode === 'scheduled' ? (
        <Style.Pickers>
          <MinimalDatePicker
            label={pickup ? 'Pickup date' : 'Delivery date'}
            value={parts.date}
            minDate={toLocalDateKey(earliest)}
            maxDate={toLocalDateKey(latest)}
            locale={locale}
            onChange={(date) => {
              if (!date) return
              const dateKey = toLocalDateKey(date)
              const nextMinimum =
                minimumTimeForDate(dateKey, referenceNow, schedulingWindow) ?? serviceStart
              const time = parts.time < nextMinimum || parts.time > serviceEnd ? nextMinimum : parts.time
              emitParts(dateKey, time)
            }}
          />
          <MinimalTimePicker
            label={pickup ? 'Pickup time' : 'Delivery time'}
            value={parts.time}
            minTime={minimumTime}
            maxTime={serviceEnd}
            intervalMinutes={intervalMinutes}
            locale={locale}
            onChange={(time) => emitParts(parts.date, time)}
          />
        </Style.Pickers>
      ) : null}
      <Style.Summary aria-live="polite">
        {pickup && !value ? 'Collect as soon as possible' : formatScheduled(value || undefined)}
        {mode === 'scheduled' ? ` · ${Intl.DateTimeFormat().resolvedOptions().timeZone}` : ''}
      </Style.Summary>
    </Style.Wrap>
  )
}
